import { z } from "zod";
import { taskChannel } from "../../drizzle/taskTypes.js";
import { attachmentCategoryWithContainersSchema } from "../../workflows/document-categories.js";
import { createEmailTaskSchema } from "../../workflows/email-tasks.js";
import { CLAIMINI_ID_ENTITY_NAME, attachmentSchema } from "../../workflows/heidelbot-entities.js";
import { withStepMetadata } from "./step-metadata.js";
/**
 * Damage Appraisal Data Schema - derived from the Heidelbot inbound contract.
 * Contains all fields from createEmailTaskSchema with attachments defaulting to [].
 * The backend stores it as step input:
 *   input: { ...data, channel: "email", previousStepId: systemStep.id }
 */
export const damageAppraisalDataSchema = createEmailTaskSchema.omit({ type: true }).extend({
    channel: z.literal(taskChannel.EMAIL),
    attachments: z.array(attachmentSchema).default([]),
});
/** Damage Appraisal Data as the damage_appraisal_check step receives it. */
export const damageAppraisalDataWithMetadataSchema = withStepMetadata(damageAppraisalDataSchema);
/**
 * User-verified damage appraisal values - typed form output
 * These are the values the user has reviewed and potentially corrected
 */
export const damageAppraisalVerifiedValuesSchema = z.object({
    claiminiId: z.string(),
    expertReportCompany: z.string(),
    creationDate: z.iso.date(),
    dateOfReceipt: z.iso.date(),
    durationForRepair: z.int(),
    replacementPeriod: z.int(),
    salvageExchange: z.boolean(),
    endDateSalvageExchange: z.iso.date().optional(),
    // Costs (stored in cents for precision)
    costsOfRepair: z.int(),
    replacementValue: z.int(),
    remainingValue: z.int(),
    impairment: z.int(),
    lossOfUse: z.int(),
    // Optional notes from reviewer
    notes: z.string().optional(),
    // Whole km from the report. Optional because older records lack it.
    mileage: z.int().nonnegative("Der Kilometerstand darf nicht negativ sein").optional(),
    // Per-attachment user-selected document categories (forwarded to document-manager).
    // Defaulted for backward compatibility: records persisted before this field was
    // introduced lack it, and several read-back paths safeParse them.
    attachmentCategories: z.array(attachmentCategoryWithContainersSchema).default([]),
});
/** Entity names extracted by Heidelbot for the damage appraisal workflow. */
export const damageAppraisalEntityNames = {
    CLAIMINI_ID: CLAIMINI_ID_ENTITY_NAME,
    EXPERT_REPORT_COMPANY: "expertReportCompany",
    CREATION_DATE: "creationDate",
    DATE_OF_RECEIPT: "dateOfReceipt",
    DURATION_FOR_REPAIR: "durationForRepair",
    REPLACEMENT_PERIOD: "replacementPeriod",
    SALVAGE_EXCHANGE: "salvageExchange",
    END_DATE_SALVAGE_EXCHANGE: "endDateSalvageExchange",
    COSTS_OF_REPAIR: "costsOfRepair",
    REPLACEMENT_VALUE: "replacementValue",
    REMAINING_VALUE: "remainingValue",
    IMPAIRMENT: "impairment",
    LOSS_OF_USE: "lossOfUse",
    MILEAGE: "mileage",
};
/**
 * Damage appraisal checked data - result of user review
 * Contains both original AI data and user-verified values
 */
export const damageAppraisalCheckedDataSchema = z.object({
    // Original AI-extracted data (for reference/auditing)
    originalData: damageAppraisalDataWithMetadataSchema,
    // User-verified values (typed form output)
    verifiedValues: damageAppraisalVerifiedValuesSchema,
    // Review metadata
    reviewedAt: z.iso.datetime(),
});
/** Damage appraisal checked data as the follow-up step receives it. */
export const damageAppraisalCheckedDataWithMetadataSchema = withStepMetadata(damageAppraisalCheckedDataSchema);
/**
 * The reviewer's decision on the docs-only path: the incident id plus the per-attachment
 * categories (no claim decision, no verified field values).
 */
export const damageAppraisalDocumentsOnlyVerifiedValuesSchema = z.object({
    claiminiId: z.string().min(1),
    // At least one attachment — linking documents is the sole purpose of this path, so an
    // empty list would create a no-op step. The UI already disables the button at zero.
    attachmentCategories: z.array(attachmentCategoryWithContainersSchema).min(1),
});
/**
 * Documents-only checked data — the reduced result the check step produces
 * when the reviewer only links the email's attachments to the existing
 * ContainerExpertReport (no claim decision, no verified field values, no section).
 * Only the incident id and the per-attachment categories are carried; the rest of
 * the check step form is intentionally ignored on this path.
 */
export const damageAppraisalDocumentsOnlyCheckedDataSchema = z.object({
    originalData: damageAppraisalDataWithMetadataSchema,
    verifiedValues: damageAppraisalDocumentsOnlyVerifiedValuesSchema,
    reviewedAt: z.iso.datetime(),
});
/** Documents-only step input — the check step's result, as every follow-up step receives it. */
export const damageAppraisalDocumentsOnlyInputSchema = withStepMetadata(damageAppraisalDocumentsOnlyCheckedDataSchema);
export const damageAppraisalReleaseResultSchema = z.unknown();
export const damageAppraisalExternalRepairReleaseResultSchema = z.unknown();
export const damageAppraisalTotalLossResultSchema = z.unknown();
export const damageAppraisalDocumentsOnlyResultSchema = z.unknown();
